import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
  type MutableRefObject,
} from "react";
import Cropper, {
  type Area,
  type Point,
  type MediaSize,
} from "react-easy-crop";
import imageCompression from "browser-image-compression";
import {
  Upload,
  Trash2,
  Image as ImageIcon,
  Loader2,
  Plus,
  Minus,
  RotateCcw,
  RotateCw,
  Move,
  RefreshCcw,
  Bug,
} from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { profilePictureService } from "@/services/profilePicture.service";
import { getAccessToken } from "@/utils/accessTokenStore";

/**
 * ProfilePictureUploader
 *
 * Provides a client-side profile picture editor (crop, zoom, rotate) with:
 * - live avatar preview while editing
 * - client-side cropping using Canvas (rotation-aware)
 * - AVIF-first compression with JPEG fallback
 * - authenticated upload to /users/me/profile-picture
 * - registration upload to /auth/register/profile-picture
 * - optional removal via DELETE /users/me/profile-picture
 *
 * Public API is exposed through an imperative ref:
 * - openFileDialog()
 * - uploadPendingImage()
 * - hasPendingImage()
 * - removeImage()
 *
 * inspired by https://dev.to/sukanta47/cropping-uploading-profile-pictures-in-react-with-typescript-and-react-easy-crop-5dl9
 */

interface ProfilePictureUploaderProps {
  imageUrl?: string;
  onUploadSuccess?: () => void;
  onRemoveSuccess?: () => void;
  enableUpload?: boolean;
  showUploadButton?: boolean;
  showActionButtons?: boolean;
  showPreview?: boolean;
  showHint?: boolean;
  autoSaveOnExit?: boolean;
  onEditingChange?: (editing: boolean) => void;
  onPreviewChange?: (previewUrl: string | null) => void;
}

export interface ProfilePictureUploaderHandle {
  openFileDialog: () => void;
  uploadPendingImage: (options?: { registrationToken?: string | null }) => Promise<boolean>;
  hasPendingImage: () => boolean;
  removeImage: () => Promise<boolean>;
}

const MAX_ORIGINAL_SIZE = 10 * 1024 * 1024;
const MAX_UPLOAD_SIZE = 300 * 1024;
const MAX_DIMENSION = 512;
const AUTO_SAVE_THROTTLE_MS = 1000;

// Ensures the cropper has a real rendered height so crop math never becomes NaN.
const CROPPER_HEIGHT_PX = 256;

// Vite DEV check + persisted toggle
const isDev =
  typeof import.meta !== "undefined" && Boolean(import.meta.env?.DEV);
let debugAdminAllowed = false;
const isDebugEnabled = () =>
  isDev &&
  debugAdminAllowed &&
  typeof window !== "undefined" &&
  (localStorage.getItem("ppDebug") === "1" ||
    new URLSearchParams(window.location.search).has("ppDebug"));

const dbg = (...args: any[]) => {
  if (!isDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

const dbgGroup = (title: string, fn: () => void) => {
  if (!isDebugEnabled()) return;
  // eslint-disable-next-line no-console
  console.groupCollapsed(title);
  try {
    fn();
  } finally {
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

const hasAuthToken = () => Boolean(getAccessToken());

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = (err) => reject(err);
    image.src = url;
  });

/**
 * Canvas -> Blob with a robust fallback for browsers where toBlob can return null.
 */
const canvasToBlobSafe = async (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob> => {
  if (!canvas.width || !canvas.height) {
    throw new Error("blob-conversion-failed");
  }

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob((b) => resolve(b), type, quality);
  });

  if (blob) return blob;

  // Fallback: dataURL -> fetch -> blob
  try {
    const dataUrl = canvas.toDataURL(type, quality);
    const res = await fetch(dataUrl);
    const b = await res.blob();
    if (!b || !b.size) throw new Error("blob-conversion-failed");
    return b;
  } catch {
    throw new Error("blob-conversion-failed");
  }
};

/**
 * Crops an image using rotation-aware canvas transforms.
 * Returns a JPEG blob; compression/transcoding is done afterwards.
 */
const cropImageToJpegBlob = async (
  imageSrc: string,
  croppedPixels: Area,
  rotation: number
): Promise<Blob> => {
  // Catches both 0 and NaN (NaN is falsy in JS)
  if (!croppedPixels?.width || !croppedPixels?.height) {
    throw new Error("no-selection");
  }

  const image = await createImage(imageSrc);
  const radians = (rotation * Math.PI) / 180;

  const rotatedWidth =
    Math.abs(Math.cos(radians) * image.width) +
    Math.abs(Math.sin(radians) * image.height);
  const rotatedHeight =
    Math.abs(Math.sin(radians) * image.width) +
    Math.abs(Math.cos(radians) * image.height);

  // 1) draw rotated full image
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no-context");

  canvas.width = Math.ceil(rotatedWidth);
  canvas.height = Math.ceil(rotatedHeight);

  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(radians);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);
  ctx.setTransform(1, 0, 0, 1, 0, 0);

  // 2) extract cropped region
  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");
  if (!croppedCtx) throw new Error("no-context");

  croppedCanvas.width = Math.max(1, Math.floor(croppedPixels.width));
  croppedCanvas.height = Math.max(1, Math.floor(croppedPixels.height));

  croppedCtx.drawImage(
    canvas,
    croppedPixels.x,
    croppedPixels.y,
    croppedPixels.width,
    croppedPixels.height,
    0,
    0,
    croppedPixels.width,
    croppedPixels.height
  );

  return canvasToBlobSafe(croppedCanvas, "image/jpeg", 0.92);
};

const ProfilePictureUploader = forwardRef<
  ProfilePictureUploaderHandle,
  ProfilePictureUploaderProps
>(
  (
    {
      imageUrl,
      onUploadSuccess,
      onRemoveSuccess,
      enableUpload = true,
      showUploadButton = true,
      showActionButtons = true,
      showPreview = true,
      showHint = true,
      autoSaveOnExit = false,
      onEditingChange,
      onPreviewChange,
    },
    ref
  ) => {
  const { t } = useApp();
  const { user } = useAuth();
  const isAdmin = (user?.role ?? "").toUpperCase() === "ADMIN";
  debugAdminAllowed = isAdmin;

  // Source for editing (object URL). Preview shows either server URL or locally generated preview.
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(imageUrl ?? "");

  // Editor state
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);

  // Cropped pixel rectangle (keep a ref so upload always reads the latest value)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });
  const croppedAreaPixelsRef = useRef<Area>(croppedAreaPixels);

  // Cached final upload file (invalidated by any editor change)
  const [croppedBlob, setCroppedBlob] = useState<File | null>(null);

  const [uploading, setUploading] = useState(false);

  // DOM & URL housekeeping
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cropperWrapperRef = useRef<HTMLDivElement | null>(null);

  const imageObjectUrlRef = useRef<string | null>(null);
  const livePreviewObjectUrlRef = useRef<string | null>(null);
  const autoSaveCompletedRef = useRef(false);
  const autoSaveInFlightRef = useRef(false);
  const autoSaveRafRef = useRef<number | null>(null);
  const lastAutoSaveAtRef = useRef(0);
  const autoSaveReadyRef = useRef(false);

  // Internal flags/jobs
  const mediaLoadedRef = useRef(false);
  const livePreviewJobRef = useRef(0);

  // Debug state/counters (DEV only)
  const debugCountsRef = useRef({
    mediaLoaded: 0,
    cropAreaChange: 0,
    cropComplete: 0,
    cropChange: 0,
    zoomChange: 0,
    rotationChange: 0,
  });
  const [debugSnapshot, setDebugSnapshot] = useState<any>(null);

  const cleanupObjectUrl = (refObj: MutableRefObject<string | null>) => {
    if (refObj.current) {
      URL.revokeObjectURL(refObj.current);
      refObj.current = null;
    }
  };

  const takeDebugSnapshot = useCallback(
    (label: string) => {
      if (!isDebugEnabled()) return;

      const wrapper = cropperWrapperRef.current;
      const rect = wrapper?.getBoundingClientRect();
      const cs = wrapper ? window.getComputedStyle(wrapper) : null;

      setDebugSnapshot({
        label,
        time: new Date().toISOString(),
        wrapperRect: rect
          ? { w: Math.round(rect.width), h: Math.round(rect.height) }
          : null,
        wrapperComputedHeight: cs?.height ?? null,
        crop,
        zoom,
        rotation,
        pixels: croppedAreaPixelsRef.current,
        counts: { ...debugCountsRef.current },
        mediaLoaded: mediaLoadedRef.current,
      });
    },
    [crop, zoom, rotation]
  );

  const resetEditor = useCallback(() => {
    // Reset the editor controls and invalidate any cached upload blob
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);

    const empty = { height: 0, width: 0, x: 0, y: 0 };
    setCroppedAreaPixels(empty);
    croppedAreaPixelsRef.current = empty;

    setCroppedBlob(null);
    cleanupObjectUrl(livePreviewObjectUrlRef);

    mediaLoadedRef.current = false;

    if (isDebugEnabled()) takeDebugSnapshot("reset");
  }, [takeDebugSnapshot]);

  const discardChanges = useCallback(() => {
    cleanupObjectUrl(imageObjectUrlRef);
    cleanupObjectUrl(livePreviewObjectUrlRef);
    setImageSrc(null);
    setPreviewUrl(imageUrl ?? "");
    autoSaveReadyRef.current = false;
    resetEditor();
  }, [imageUrl, resetEditor]);

  // Keep preview in sync with external image URL changes
  useEffect(() => {
    if (imageUrl) {
      cleanupObjectUrl(imageObjectUrlRef);
      cleanupObjectUrl(livePreviewObjectUrlRef);
      setImageSrc(null);
      setPreviewUrl(imageUrl);
      resetEditor();
    } else {
      setPreviewUrl("");
    }
    autoSaveReadyRef.current = false;
    onEditingChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  // Cleanup on unmount
  useEffect(() => {
    onEditingChange?.(false);
    return () => {
      const shouldDeferCleanup =
        autoSaveOnExit &&
        (autoSaveReadyRef.current ||
          autoSaveInFlightRef.current ||
          autoSaveRafRef.current !== null);

      if (!shouldDeferCleanup || typeof window === "undefined") {
        cleanupObjectUrl(imageObjectUrlRef);
        cleanupObjectUrl(livePreviewObjectUrlRef);
        return;
      }

      const maxAttempts = 10;
      const attemptCleanup = (attempt = 1) => {
        if (
          autoSaveInFlightRef.current ||
          autoSaveRafRef.current !== null
        ) {
          if (attempt < maxAttempts) {
            window.setTimeout(() => attemptCleanup(attempt + 1), 1000);
            return;
          }
        }
        cleanupObjectUrl(imageObjectUrlRef);
        cleanupObjectUrl(livePreviewObjectUrlRef);
      };

      window.setTimeout(() => attemptCleanup(), AUTO_SAVE_THROTTLE_MS * 2);
    };
  }, [autoSaveOnExit, onEditingChange]);

  // Notify when entering/leaving edit mode (image selected vs cleared)
  useEffect(() => {
    onEditingChange?.(Boolean(imageSrc));
  }, [imageSrc, onEditingChange]);

  useEffect(() => {
    onPreviewChange?.(imageSrc ? previewUrl || null : null);
  }, [imageSrc, previewUrl, onPreviewChange]);

  // Imperative API
  useImperativeHandle(ref, () => ({
    openFileDialog: () => fileInputRef.current?.click(),
    uploadPendingImage: (options?: { registrationToken?: string | null }) =>
      enableUpload
        ? handleUpload({
            silentIfMissing: true,
            registrationToken: options?.registrationToken,
          })
        : Promise.resolve(false),
    hasPendingImage: () => Boolean(croppedBlob || imageSrc),
    removeImage: () => handleRemove(true),
  }));

  const invalidateCachedBlob = useCallback(() => {
    autoSaveCompletedRef.current = false;
    setCroppedBlob(null);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    autoSaveCompletedRef.current = false;
    autoSaveReadyRef.current = true;

    if (file.size > MAX_ORIGINAL_SIZE) {
      toast.error(t("profilePicture.maxOriginal"));
      return;
    }

    // Replace any previous object URLs
    cleanupObjectUrl(imageObjectUrlRef);
    cleanupObjectUrl(livePreviewObjectUrlRef);

    const url = URL.createObjectURL(file);
    imageObjectUrlRef.current = url;

    resetEditor();

    // Start editing immediately; avatar preview defaults to selected image
    setImageSrc(url);
    setPreviewUrl(url);

    if (isDebugEnabled()) {
      dbgGroup("[PP] file selected", () => {
        dbg({ name: file.name, type: file.type, size: file.size, url });
      });
      setTimeout(() => takeDebugSnapshot("afterFileSelect"), 0);
    }

    // Basic file validity check
    createImage(url).catch(() => {
      cleanupObjectUrl(imageObjectUrlRef);
      setImageSrc(null);
      setPreviewUrl("");
      toast.error(t("profilePicture.invalidFile"));
    });
  };

  const onMediaLoaded = useCallback(
    (size: MediaSize) => {
      mediaLoadedRef.current = true;
      debugCountsRef.current.mediaLoaded += 1;

      if (isDebugEnabled()) {
        dbgGroup("[PP] onMediaLoaded", () => dbg(size));
        takeDebugSnapshot("mediaLoaded");
      }

      // Force a first crop computation
      requestAnimationFrame(() => {
        setZoom((z) => Number((z + 0.0001).toFixed(4)));
        requestAnimationFrame(() => setZoom(1));
      });
    },
    [takeDebugSnapshot]
  );

  const handleZoomClick = (mode: "zoomin" | "zoomout") => {
    debugCountsRef.current.zoomChange += 1;
    setZoom((prev) =>
      mode === "zoomin"
        ? Math.min(3, Number((prev + 0.1).toFixed(2)))
        : Math.max(1, Number((prev - 0.1).toFixed(2)))
    );
    invalidateCachedBlob();
    if (isDebugEnabled()) takeDebugSnapshot("zoomClick");
  };

  const handleRotationChange = (direction: "left" | "right") => {
    debugCountsRef.current.rotationChange += 1;
    setRotation((prev) => {
      const next = direction === "left" ? prev - 90 : prev + 90;
      return next >= 360 || next <= -360 ? 0 : next;
    });
    invalidateCachedBlob();
    if (isDebugEnabled()) takeDebugSnapshot("rotateClick");
  };

  const compressBlob = async (
    file: File,
    type: "image/avif" | "image/jpeg"
  ) => {
    const compressed = await imageCompression(file, {
      maxSizeMB: MAX_UPLOAD_SIZE / (1024 * 1024),
      maxWidthOrHeight: MAX_DIMENSION,
      fileType: type,
      initialQuality: type === "image/avif" ? 0.75 : 0.85,
      useWebWorker: true,
      alwaysKeepResolution: true,
    });

    const normalized =
      compressed instanceof File
        ? compressed
        : new File(
            [compressed],
            type === "image/avif" ? "profile.avif" : "profile.jpg",
            {
              type,
            }
          );

    if (normalized.size > MAX_UPLOAD_SIZE) throw new Error("too-large");
    return normalized;
  };

  const prepareUploadFile = async (): Promise<File> => {
    if (!imageSrc) throw new Error("no-selection");

    const pixels = croppedAreaPixelsRef.current;

    if (isDebugEnabled()) {
      dbgGroup("[PP] prepareUploadFile", () =>
        dbg({ pixels, mediaLoaded: mediaLoadedRef.current })
      );
      takeDebugSnapshot("prepareUploadFile");
    }

    // Catches 0 and NaN
    if (!pixels?.width || !pixels?.height) throw new Error("no-selection");

    // Crop into a JPEG blob (rotation-aware), then compress/transcode
    const jpegBlob = await cropImageToJpegBlob(imageSrc, pixels, rotation);
    const baseFile = new File([jpegBlob], "profile.jpg", {
      type: "image/jpeg",
    });

    try {
      // Prefer AVIF for size/quality
      return await compressBlob(baseFile, "image/avif");
    } catch {
      // Fallback when AVIF encode isn't supported
      return await compressBlob(baseFile, "image/jpeg");
    }
  };

  const ensureCroppedBlob = async () => {
    if (croppedBlob) return croppedBlob;
    const prepared = await prepareUploadFile();
    setCroppedBlob(prepared);
    return prepared;
  };

  /**
   * Live avatar preview: re-render a cropped blob whenever editor settings change.
   * Debounced + job id prevents races and flicker.
   */
  useEffect(() => {
    if (!imageSrc) return;
    if (!mediaLoadedRef.current) return;

    const pixels = croppedAreaPixelsRef.current;
    if (!pixels?.width || !pixels?.height) return;

    const jobId = ++livePreviewJobRef.current;

    const timer = window.setTimeout(async () => {
      try {
        const blob = await cropImageToJpegBlob(
          imageSrc,
          croppedAreaPixelsRef.current,
          rotation
        );
        if (livePreviewJobRef.current !== jobId) return;

        cleanupObjectUrl(livePreviewObjectUrlRef);
        const url = URL.createObjectURL(blob);
        livePreviewObjectUrlRef.current = url;
        setPreviewUrl(url);

        if (isDebugEnabled())
          dbgGroup("[PP] preview updated", () => dbg({ url }));
      } catch (e) {
        if (isDebugEnabled()) dbgGroup("[PP] preview failed", () => dbg(e));
      }
    }, 120);

    return () => window.clearTimeout(timer);
  }, [imageSrc, crop, zoom, rotation]);

  const handleUpload = async (
    options: {
      silentIfMissing?: boolean;
      registrationToken?: string | null;
      suppressSuccessCallback?: boolean;
      suppressSuccessToast?: boolean;
    } = {}
  ) => {
    const { silentIfMissing, registrationToken, suppressSuccessCallback } =
      options;
    if (!enableUpload) return false;

    const trimmedToken = registrationToken?.trim();
    const useRegistrationToken = Boolean(trimmedToken);

    if (!useRegistrationToken && !hasAuthToken()) {
      if (!silentIfMissing) toast.warning(t("profilePicture.uploadWarning"));
      return false;
    }

    try {
      autoSaveInFlightRef.current = true;
      setUploading(true);

      const prepared = await ensureCroppedBlob();
      if (useRegistrationToken && trimmedToken) {
        await profilePictureService.uploadForRegistration(trimmedToken, prepared);
      } else {
        await profilePictureService.uploadForCurrentUser(prepared);
      }

      autoSaveCompletedRef.current = true;
      if (!options.suppressSuccessToast) {
        toast.success(t("profilePicture.uploadSuccess"));
      }
      if (!suppressSuccessCallback) {
        onUploadSuccess?.();
      }
      return true;
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error("ProfilePictureUploader upload failed:", error);

      if (isDebugEnabled()) {
        dbgGroup("[PP] upload failed", () => {
          dbg("error:", error);
          dbg("pixels:", croppedAreaPixelsRef.current);
        });
        takeDebugSnapshot("uploadFailed");
      }

      if (
        error?.message === "no-selection" ||
        error?.message === "blob-conversion-failed"
      ) {
        if (!silentIfMissing) toast.error(t("profilePicture.uploadFailed"));
      } else if (error?.message === "too-large") {
        toast.error(t("profilePicture.uploadFailedDifferent"));
      } else if (error?.response?.status) {
        const status = error.response.status;
        if (status === 401) toast.error(t("profilePicture.authExpired"));
        else if (status >= 500)
          toast.error(t("profilePicture.uploadNetworkError"));
        else toast.error(t("profilePicture.uploadFailedDifferent"));
      } else {
        toast.error(t("profilePicture.uploadNetworkError"));
      }
      return false;
    } finally {
      autoSaveInFlightRef.current = false;
      setUploading(false);
    }
  };

  const autoSavePending = useCallback(() => {
    if (!autoSaveOnExit || !enableUpload) return;
    if (!imageSrc && !croppedBlob) return;
    if (!croppedBlob) {
      const pixels = croppedAreaPixelsRef.current;
      if (!pixels?.width || !pixels?.height) return;
    }
    if (autoSaveCompletedRef.current || autoSaveInFlightRef.current) return;
    if (autoSaveRafRef.current !== null) return;
    const now = Date.now();
    if (now - lastAutoSaveAtRef.current < AUTO_SAVE_THROTTLE_MS) return;
    autoSaveRafRef.current = window.requestAnimationFrame(async () => {
      autoSaveRafRef.current = null;
      if (!autoSaveOnExit || !enableUpload) return;
      if (!imageSrc && !croppedBlob) return;
      if (autoSaveCompletedRef.current || autoSaveInFlightRef.current) return;
      const attemptAt = Date.now();
      if (attemptAt - lastAutoSaveAtRef.current < AUTO_SAVE_THROTTLE_MS) return;
      lastAutoSaveAtRef.current = attemptAt;
      autoSaveInFlightRef.current = true;
      const success = await handleUpload({
        silentIfMissing: true,
        suppressSuccessCallback: true,
        suppressSuccessToast: true,
      });
      autoSaveInFlightRef.current = false;
      if (success) {
        autoSaveCompletedRef.current = true;
      }
    });
  }, [autoSaveOnExit, enableUpload, imageSrc, croppedBlob, handleUpload]);

  /**
   * Keeps pixel crop rect updated continuously; also invalidates cached upload blob.
   */
  const updatePixels = useCallback(
    (area: Area, pixels: Area, source: "areaChange" | "complete") => {
      setCroppedAreaPixels(pixels);
      croppedAreaPixelsRef.current = pixels;
      invalidateCachedBlob();
      if (autoSaveReadyRef.current && pixels?.width && pixels?.height) {
        autoSaveReadyRef.current = false;
        autoSavePending();
      }

      if (!isDebugEnabled()) return;

      if (source === "areaChange") debugCountsRef.current.cropAreaChange += 1;
      else debugCountsRef.current.cropComplete += 1;

      // Avoid spamming snapshots too much
      if (
        debugCountsRef.current.cropAreaChange % 10 === 0 ||
        source === "complete"
      ) {
        takeDebugSnapshot(source);
      }
    },
    [autoSavePending, invalidateCachedBlob, takeDebugSnapshot]
  );

  const handleRemove = async (silent?: boolean) => {
    const clearLocal = () => {
      cleanupObjectUrl(imageObjectUrlRef);
      cleanupObjectUrl(livePreviewObjectUrlRef);
      setPreviewUrl("");
      setImageSrc(null);
      resetEditor();
    };

    const shouldSkipServer = !enableUpload || !imageUrl || !hasAuthToken();
    if (shouldSkipServer) {
      clearLocal();
      if (!silent) toast.success(t("profilePicture.removeSuccess"));
      onRemoveSuccess?.();
      return true;
    }

    try {
      setUploading(true);
      await profilePictureService.removeForCurrentUser();
      clearLocal();
      if (!silent) toast.success(t("profilePicture.removeSuccess"));
      onRemoveSuccess?.();
      return true;
    } catch (error: any) {
      if (!silent)
        toast.error(t(error?.response?.data?.message ?? "common.genericError"));
      return false;
    } finally {
      setUploading(false);
    }
  };

  const rotationLabel = useMemo(
    () => `${(((rotation % 360) + 360) % 360).toString()}°`,
    [rotation]
  );

  const debugEnabled = isDebugEnabled();
  const showDebugButton = isDev && isAdmin;

  const saveButtonClassName = useMemo(
    () =>
      debugEnabled
        ? "gap-2 ring-2 ring-ring ring-offset-2 ring-offset-background"
        : "gap-2",
    [debugEnabled]
  );
  const handleSaveClick = () => {
    if (
      autoSaveCompletedRef.current &&
      !autoSaveInFlightRef.current &&
      onUploadSuccess
    ) {
      onUploadSuccess();
      return;
    }
    void handleUpload();
  };

  useEffect(() => {
    if (!autoSaveOnExit || typeof window === "undefined") return;
    const handlePageHide = () => autoSavePending();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") autoSavePending();
    };
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (autoSaveRafRef.current !== null) {
        window.cancelAnimationFrame(autoSaveRafRef.current);
        autoSaveRafRef.current = null;
      }
      autoSavePending();
    };
  }, [autoSaveOnExit, autoSavePending]);

  return (
    <div className="space-y-4">
      {(showActionButtons || (imageSrc && showPreview)) && (
        <div className="flex items-center gap-4 flex-wrap">
          {imageSrc && showPreview && (
            <div
              className="w-24 h-24 rounded-full overflow-hidden border border-border"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--surface) 70%, var(--background))",
              }}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={t("profilePicture.previewAlt")}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{
                    color:
                      "color-mix(in srgb, var(--foreground) 70%, var(--background))",
                  }}
                >
                  <ImageIcon className="w-6 h-6" />
                </div>
              )}
            </div>
          )}

          {showActionButtons && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <Upload className="w-4 h-4" />
                {previewUrl
                  ? t("profilePicture.change")
                  : t("profilePicture.select")}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => handleRemove()}
                disabled={uploading}
              >
                <Trash2 className="w-4 h-4" />
                {t("profilePicture.remove")}
              </Button>

              {/* Only show debug toggle to logged-in admins (DEV only) */}
              {showDebugButton && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const next =
                      localStorage.getItem("ppDebug") === "1" ? "0" : "1";
                    if (next === "1") localStorage.setItem("ppDebug", "1");
                    else localStorage.removeItem("ppDebug");
                    window.location.reload();
                  }}
                  className={
                    debugEnabled
                      ? "gap-2 ring-2 ring-ring ring-offset-2 ring-offset-background"
                      : "gap-2"
                  }
                  title="Toggle ppDebug (DEV only)"
                >
                  <Bug className="w-4 h-4" />
                  Debug
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {imageSrc && (
        <div className="space-y-3">
          <div
            className="space-y-4 rounded-lg border border-border p-4"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--surface) 60%, var(--background))",
            }}
          >
            {/* Cropper area: enforce a real height so crop math produces valid pixel values */}
            <div
              ref={cropperWrapperRef}
              className="relative w-full rounded-lg overflow-hidden"
              style={{
                height: CROPPER_HEIGHT_PX,
                minHeight: CROPPER_HEIGHT_PX,
                touchAction: "none",
                userSelect: "none",
                WebkitUserSelect: "none",
                cursor: "grab",
                backgroundColor:
                  "color-mix(in srgb, var(--surface) 70%, var(--background))",
              }}
            >
              <Cropper
                key={imageSrc} // fresh mount for new images
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                minZoom={1}
                maxZoom={3}
                onCropChange={(c) => {
                  debugCountsRef.current.cropChange += 1;
                  setCrop(c);
                  invalidateCachedBlob();
                }}
                onZoomChange={(z) => {
                  debugCountsRef.current.zoomChange += 1;
                  setZoom(z);
                  invalidateCachedBlob();
                }}
                onRotationChange={(r) => {
                  debugCountsRef.current.rotationChange += 1;
                  setRotation(r);
                  invalidateCachedBlob();
                }}
                onCropAreaChange={(area, pixels) =>
                  updatePixels(area, pixels, "areaChange")
                }
                onCropComplete={(area, pixels) =>
                  updatePixels(area, pixels, "complete")
                }
                onMediaLoaded={onMediaLoaded}
                style={{
                  containerStyle: {
                    width: "100%",
                    height: "100%",
                    touchAction: "none",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    cursor: "grab",
                  },
                  mediaStyle: {
                    pointerEvents: "auto",
                  },
                }}
              />

              {/* Always-visible move hint */}
              <div
                className="absolute left-3 bottom-3 flex items-center gap-2 text-xs px-2 py-1 rounded-md"
                style={{
                  pointerEvents: "none",
                  backgroundColor:
                    "color-mix(in srgb, var(--foreground) 80%, transparent)",
                  color: "var(--background)",
                }}
              >
                <Move className="w-4 h-4" />
                <span className="font-medium">
                  {t("profilePicture.controls.move")}
                </span>
                <span className="opacity-95">
                  {t("profilePicture.controls.moveHint")}
                </span>
              </div>
            </div>

            {/* Controls */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs text-secondary">
                  {t("profilePicture.cropInstruction")}
                </span>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={resetEditor}
                  disabled={uploading}
                  className="gap-2"
                  title={t("profilePicture.controls.reset")}
                >
                  <RefreshCcw className="w-4 h-4" />
                  {t("profilePicture.controls.reset")}
                </Button>
              </div>

              {/* Zoom */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleZoomClick("zoomout")}
                    disabled={uploading}
                    title={t("profilePicture.controls.zoomOut")}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>

                  <span className="text-sm font-medium">
                    {t("profilePicture.controls.zoom")}
                  </span>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleZoomClick("zoomin")}
                    disabled={uploading}
                    title={t("profilePicture.controls.zoomIn")}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => {
                      setZoom(Number(e.target.value));
                      invalidateCachedBlob();
                    }}
                    className="w-full"
                    style={{ accentColor: "var(--accent)" }}
                    aria-label={t("profilePicture.controls.zoom")}
                    disabled={uploading}
                    title={t("profilePicture.controls.zoomSliderHint")}
                  />
                  <span className="text-xs text-secondary tabular-nums w-12 text-right">
                    {zoom.toFixed(1)}x
                  </span>
                </div>
              </div>

              {/* Rotate */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRotationChange("left")}
                    disabled={uploading}
                    title={t("profilePicture.controls.rotateLeft")}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>

                  <span className="text-sm font-medium">
                    {t("profilePicture.controls.rotate")}
                  </span>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRotationChange("right")}
                    disabled={uploading}
                    title={t("profilePicture.controls.rotateRight")}
                  >
                    <RotateCw className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-secondary">
                    {t("profilePicture.controls.rotationValue")}
                  </span>
                  <span className="text-xs font-medium tabular-nums">
                    {rotationLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Save - hidden when upload is disabled or the caller opts out of manual uploads */}
            {enableUpload && showUploadButton && (
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={handleSaveClick}
                  className={saveButtonClassName}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("profilePicture.uploading")}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {t("profilePicture.upload")}
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={discardChanges}
                  disabled={uploading}
                >
                  {t("common.cancel")}
                </Button>
              </div>
            )}

            {/* DEV debug panel stays only when debug is enabled AND admin */}
            {debugEnabled && (
            <div
              className="rounded-lg border border-border p-3 text-xs"
              style={{
                backgroundColor:
                  "color-mix(in srgb, var(--foreground) 6%, var(--surface))",
              }}
            >
                <div className="font-semibold mb-2">ProfilePicture Debug</div>
                <pre className="whitespace-pre-wrap break-words">
                  {JSON.stringify(
                    {
                      crop,
                      zoom,
                      rotation,
                      pixels: croppedAreaPixelsRef.current,
                      mediaLoaded: mediaLoadedRef.current,
                      counts: debugCountsRef.current,
                      snapshot: debugSnapshot,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {showHint && (
        <p className="text-xs text-secondary">{t("profilePicture.hint")}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
});

ProfilePictureUploader.displayName = "ProfilePictureUploader";
export default ProfilePictureUploader;
